import { useState, useEffect } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts'
import {
  getBudgetItems,
  getBudgetStats,
  getUserBudget,
  updateUserBudget,
  createBudgetItem,
  updateBudgetItem,
  deleteBudgetItem,
  deleteCategory
} from '../../services/budgetService'
import { showAlert, showConfirm } from '../../services/dialogService'
import InfoButton from '../../components/InfoButton/InfoButton'
import Loader from '../../components/Loader/Loader'
import './Section.css'
import './BudgetManager.css'

// Default categories (not used for auto-creation, just for reference)
const DEFAULT_CATEGORIES = [
  { name: 'Ceremony', icon: '', key: 'ceremony' },
  { name: 'Banquet', icon: '', key: 'banquet' },
  { name: 'Music', icon: '', key: 'music' },
  { name: 'Invitations', icon: '', key: 'invitations' },
  { name: 'Wedding Details', icon: '', key: 'wedding_details' },
  { name: 'Flowers & Decoration', icon: '', key: 'flowers_decoration' },
  { name: 'Photo & Video', icon: '', key: 'photo_video' },
  { name: 'Transport', icon: '', key: 'transport' },
  { name: 'Jewelry', icon: '', key: 'jewelry' },
  { name: 'Bride & Accessories', icon: '', key: 'bride_accessories' },
  { name: 'Groom & Accessories', icon: '', key: 'groom_accessories' },
  { name: 'Beauty & Health', icon: '', key: 'beauty_health' },
  { name: 'Honeymoon', icon: '', key: 'honeymoon' },
  { name: 'Accommodation', icon: '', key: 'accommodation' },
  { name: 'Attire', icon: '', key: 'attire' },
  { name: 'Cake', icon: '', key: 'cake' },
]

// Mensajes según porcentaje
const getBudgetMessage = (percentage) => {
  if (percentage <= 25) {
    return {
      percentage: `${percentage}% of budget`,
      message: "Early days. Save your energy (and $$) for what's coming."
    }
  } else if (percentage <= 50) {
    return {
      percentage: `${percentage}% of budget`,
      message: `${percentage}% down, ${100 - percentage}% to go. Time for a little gut check before you keep swiping.`
    }
  } else if (percentage <= 75) {
    return {
      percentage: `${percentage}% of budget`,
      message: "Heads up — you're getting close! Double-check you haven't forgotten any big expenses (rings, favours, suppliers)."
    }
  } else if (percentage <= 90) {
    return {
      percentage: `${percentage}% of budget`,
      message: "Careful — 90% gone means there's not much wiggle room left. Breathe, prioritise, and maybe skip the ice sculpture."
    }
  } else if (percentage <= 100) {
    return {
      percentage: `${percentage}% of budget`,
      message: "Budget complete ✅ — but the wedding isn't. Time to decide: trim or top up?"
    }
  } else if (percentage <= 110) {
    return {
      percentage: `${percentage}% of budget`,
      message: "Slightly over. Blame it on the flowers, or the open bar, or… well, you."
    }
  } else if (percentage <= 125) {
    return {
      percentage: `${percentage}% of budget`,
      message: "Quarter over budget. At this point, your wedding owes you a gift."
    }
  } else if (percentage <= 150) {
    return {
      percentage: `${percentage}% of budget`,
      message: "Budget? What budget? You're officially in freestyle mode now."
    }
  } else if (percentage <= 175) {
    return {
      percentage: `${percentage}% of budget`,
      message: "You've gone from 'wedding planning' to 'wedding investing.'"
    }
  } else {
    return {
      percentage: `${percentage}% of budget`,
      message: "Double the budget, double the drama. Congrats, you're running the Olympics of weddings."
    }
  }
}

// Colores para el gráfico de dona
const CHART_COLORS = [
  '#FF6B9D', '#FFB3BA', '#FFD93D', '#6BCF7F', '#4ECDC4', '#45B7D1',
  '#96CEB4', '#FFEAA7', '#DDA15E', '#BC6C25', '#A8DADC', '#457B9D',
  '#E63946', '#F77F00', '#FCBF49', '#F1FAEE'
]


function BudgetManager() {
  const { user, loading: authLoading } = useAuth()
  const [categories, setCategories] = useState({})
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [stats, setStats] = useState({
    totalBudget: 0,
    totalEstimated: 0,
    totalActual: 0,
    totalPaid: 0,
    totalPending: 0,
    remaining: 0,
    percentage: 0
  })
  const [loading, setLoading] = useState(true)
  const [showEditBudget, setShowEditBudget] = useState(false)
  const [newBudget, setNewBudget] = useState('')
  const [showNewCategory, setShowNewCategory] = useState(false)
  const [newCategoryName, setNewCategoryName] = useState('')
  const [showNewExpense, setShowNewExpense] = useState(false)
  const [newExpense, setNewExpense] = useState({
    concept: '',
    estimated_cost: '',
    actual_cost: '',
    paid: false
  })
  const [localItems, setLocalItems] = useState({}) // Estado local para ediciones en curso
  const [updatingItems, setUpdatingItems] = useState(new Set()) // Items que se están actualizando
  const [editingItems, setEditingItems] = useState(new Set()) // Items que están en modo edición

  // Helper para formatear valores (solo números, sin moneda)
  const formatCurrency = (value, decimals = 0) => {
    return parseFloat(value || 0).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
  }

  // Helper para actualizar estado y localItems
  const updateBudgetState = (itemsData, statsData) => {
    setCategories(itemsData.categories)
    setStats(statsData)
    
    // Actualizar estado local
    const updatedLocalItems = { ...localItems }
    Object.values(itemsData.categories).forEach(category => {
      category.items.forEach(item => {
        updatedLocalItems[item.id] = {
          concept: item.concept || '',
          estimated_cost: item.estimated_cost || '',
          actual_cost: item.actual_cost || '',
          paid: item.paid || false
        }
      })
    })
    setLocalItems(updatedLocalItems)
  }

  // Cargar datos
  useEffect(() => {
    if (!user) return

    const loadData = async () => {
      try {
        setLoading(true)
        const [itemsData, statsData] = await Promise.all([
          getBudgetItems(user.id),
          getBudgetStats(user.id)
        ])
        updateBudgetState(itemsData, statsData)
        
        // Seleccionar primera categoría si hay categorías y ninguna está seleccionada
        if (Object.keys(itemsData.categories).length > 0 && !selectedCategory) {
          const firstCategory = Object.keys(itemsData.categories)[0]
          setSelectedCategory(firstCategory)
        }
      } catch (error) {
        console.error('Error loading budget data:', error)
      } finally {
        // Reducido delay para mejor rendimiento
        setTimeout(() => {
          setLoading(false)
        }, 50)
      }
    }

    loadData()
  }, [user])

  // Preparar datos para el gráfico
  const chartData = Object.values(categories).map((category, index) => ({
    name: category.name,
    value: category.totalActual || category.totalEstimated || 0,
    color: CHART_COLORS[index % CHART_COLORS.length]
  })).filter(item => item.value > 0)

  const budgetMessage = getBudgetMessage(stats.percentage)

  // Handlers
  const handleUpdateBudget = async () => {
    if (!user || !newBudget || isNaN(parseFloat(newBudget))) return

    try {
      await updateUserBudget(user.id, parseFloat(newBudget))
      const updatedStats = await getBudgetStats(user.id)
      setStats(updatedStats)
      setShowEditBudget(false)
      setNewBudget('')
    } catch (error) {
      console.error('Error updating budget:', error)
      await showAlert('Error updating budget. Please try again.')
    }
  }

  const handleCreateCategory = async () => {
    if (!user || !newCategoryName.trim()) return

    try {
      // Crear un item placeholder para la categoría (con un concepto por defecto)
      await createBudgetItem(user.id, {
        category: newCategoryName.trim(),
        category_icon: '',
        concept: 'New category - Add your first expense',
        estimated_cost: 0,
        actual_cost: 0,
        paid: false
      })
      
      const itemsData = await getBudgetItems(user.id)
      const statsData = await getBudgetStats(user.id)
      updateBudgetState(itemsData, statsData)
      setSelectedCategory(newCategoryName.trim())
      setShowNewCategory(false)
      setNewCategoryName('')
    } catch (error) {
      console.error('Error creating category:', error)
      
      // Mostrar error más específico
      let errorMessage = 'Error creating category. Please try again.'
      if (error.message) {
        errorMessage = `Error: ${error.message}`
      } else if (error.code === '42501') {
        errorMessage = 'Permission denied. Please check that you have access to this wedding.'
      } else if (error.code === '23502') {
        errorMessage = 'Missing required field. Please check the category details.'
      } else if (error.details) {
        errorMessage = `Error: ${error.details}`
      }
      
      await showAlert(errorMessage)
    }
  }

  const handleCreateExpense = async () => {
    if (!user) {
      await showAlert('User not found. Please sign in again.')
      return
    }

    if (!selectedCategory) {
      await showAlert('Please select a category first.')
      return
    }

    if (!newExpense.concept || !newExpense.concept.trim()) {
      await showAlert('Please enter a concept for the expense.')
      return
    }

    const estimatedCost = parseFloat(newExpense.estimated_cost || 0)
    if (isNaN(estimatedCost)) {
      await showAlert('Estimated cost must be a valid number.')
      return
    }
    if (estimatedCost < 0) {
      await showAlert('Estimated cost cannot be negative.')
      return
    }

    let actualCost = null
    if (newExpense.actual_cost && newExpense.actual_cost.trim()) {
      actualCost = parseFloat(newExpense.actual_cost)
      if (isNaN(actualCost)) {
        await showAlert('Final cost must be a valid number.')
        return
      }
      if (actualCost < 0) {
        await showAlert('Final cost cannot be negative.')
        return
      }
    }

    try {
      await createBudgetItem(user.id, {
        category: selectedCategory,
        category_icon: categories[selectedCategory]?.icon || '',
        concept: newExpense.concept.trim(),
        estimated_cost: estimatedCost,
        actual_cost: actualCost,
        paid: newExpense.paid || false
      })

      const [itemsData, statsData] = await Promise.all([
        getBudgetItems(user.id),
        getBudgetStats(user.id)
      ])
      updateBudgetState(itemsData, statsData)
      setShowNewExpense(false)
      setNewExpense({ concept: '', estimated_cost: '', actual_cost: '', paid: false })
    } catch (error) {
      console.error('Error creating expense:', error)
      const errorMessage = error?.message || 'Unknown error creating expense.'
      const detailedError = error?.code ? `Error ${error.code}: ${errorMessage}` : errorMessage
      await showAlert(`Error creating expense: ${detailedError}\n\nPlease verify that you have a wedding configured and that all fields are valid.`)
    }
  }

  // Activar modo edición para un item
  const handleStartEdit = (item) => {
    setEditingItems(prev => new Set(prev).add(item.id))
    // Inicializar estado local con los valores actuales del item
    setLocalItems(prev => ({
      ...prev,
      [item.id]: {
        concept: item.concept || '',
        estimated_cost: item.estimated_cost || '',
        actual_cost: item.actual_cost || '',
        paid: item.paid || false
      }
    }))
  }

  // Cancelar edición y revertir cambios
  const handleCancelEdit = (itemId) => {
    setEditingItems(prev => {
      const newSet = new Set(prev)
      newSet.delete(itemId)
      return newSet
    })
    // Revertir a valores originales desde categories
    const item = Object.values(categories)
      .flatMap(cat => cat.items)
      .find(i => i.id === itemId)
    
    if (item) {
      setLocalItems(prev => ({
        ...prev,
        [itemId]: {
          concept: item.concept || '',
          estimated_cost: item.estimated_cost || '',
          actual_cost: item.actual_cost || '',
          paid: item.paid || false
        }
      }))
    }
  }

  // Actualizar estado local mientras se edita
  const handleLocalUpdate = (itemId, field, value) => {
    setLocalItems(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: value
      }
    }))
  }

  // Guardar cambios en la BD
  const handleSaveExpense = async (itemId) => {
    if (!localItems[itemId] || updatingItems.has(itemId)) return

    const localItem = localItems[itemId]

    // Validate numeric values before saving
    const estimatedCost = parseFloat(localItem.estimated_cost || 0)
    if (isNaN(estimatedCost) || estimatedCost < 0) {
      await showAlert('Estimated cost must be a valid number and non-negative.')
      return
    }

    let actualCost = null
    if (localItem.actual_cost && localItem.actual_cost !== '' && localItem.actual_cost !== null) {
      actualCost = parseFloat(localItem.actual_cost)
      if (isNaN(actualCost) || actualCost < 0) {
        await showAlert('Final cost must be a valid number and non-negative.')
        return
      }
    }

    setUpdatingItems(prev => new Set(prev).add(itemId))

    try {
      const updates = {
        concept: localItem.concept?.trim() || '',
        estimated_cost: estimatedCost,
        actual_cost: actualCost,
        paid: localItem.paid || false
      }

      await updateBudgetItem(itemId, updates)
      const [itemsData, statsData] = await Promise.all([
        getBudgetItems(user.id),
        getBudgetStats(user.id)
      ])
      updateBudgetState(itemsData, statsData)
      
      // Salir del modo edición
      setEditingItems(prev => {
        const newSet = new Set(prev)
        newSet.delete(itemId)
        return newSet
      })
    } catch (error) {
      console.error('Error updating expense:', error)
      await showAlert('Error updating expense. Please try again.')
    } finally {
      setUpdatingItems(prev => {
        const newSet = new Set(prev)
        newSet.delete(itemId)
        return newSet
      })
    }
  }

  // Para checkboxes, actualizar inmediatamente (es más rápido)
  const handleCheckboxChange = async (itemId, checked) => {
    handleLocalUpdate(itemId, 'paid', checked)
    
    try {
      await updateBudgetItem(itemId, { paid: checked })
      const [itemsData, statsData] = await Promise.all([
        getBudgetItems(user.id),
        getBudgetStats(user.id)
      ])
      updateBudgetState(itemsData, statsData)
    } catch (error) {
      console.error('Error updating expense:', error)
      // Revertir cambio local si falla
      handleLocalUpdate(itemId, 'paid', !checked)
      await showAlert('Error updating expense. Please try again.')
    }
  }

  const handleDeleteExpense = async (itemId) => {
    const ok = await showConfirm('Are you sure you want to delete this expense?', { title: 'Delete expense', confirmLabel: 'Delete', cancelLabel: 'Cancel', variant: 'danger' })
    if (!ok) return

    try {
      await deleteBudgetItem(itemId)
      const [itemsData, statsData] = await Promise.all([
        getBudgetItems(user.id),
        getBudgetStats(user.id)
      ])
      updateBudgetState(itemsData, statsData)
    } catch (error) {
      console.error('Error deleting expense:', error)
      await showAlert('Error deleting expense. Please try again.')
    }
  }

  const handleDeleteCategory = async () => {
    if (!user || !selectedCategory) return
    const ok = await showConfirm(`Are you sure you want to delete the category "${selectedCategory}" and all its expenses?`, { title: 'Delete category', confirmLabel: 'Delete', cancelLabel: 'Cancel', variant: 'danger' })
    if (!ok) return

    try {
      await deleteCategory(user.id, selectedCategory)
      const [itemsData, statsData] = await Promise.all([
        getBudgetItems(user.id),
        getBudgetStats(user.id)
      ])
      updateBudgetState(itemsData, statsData)
      
      // Seleccionar otra categoría o ninguna
      const remainingCategories = Object.keys(itemsData.categories)
      setSelectedCategory(remainingCategories.length > 0 ? remainingCategories[0] : null)
    } catch (error) {
      console.error('Error deleting category:', error)
      await showAlert('Error deleting category. Please try again.')
    }
  }

  // Mostrar loader mientras se cargan los datos o mientras se verifica la autenticación
  if (authLoading || loading) {
    return <Loader />
  }

  const selectedCategoryData = selectedCategory ? categories[selectedCategory] : null

  return (
    <div className="dashboard-section budget-manager">
      <h1>
        BUDGET MANAGER
        <InfoButton
          title="Budget Manager"
          content={
            <div>
              <p>
                The not-so-fun-but-super-necessary part. Add all your expenses, organise them by category, 
                and keep an eye on the chart — it'll tell you if you're still safe or already living dangerously.
              </p>
              <p>
                <strong>Numbers only, please. No dots or commas — keep it simple so we can do the math for you.</strong>
              </p>
              <p>
                <strong>Features:</strong>
              </p>
              <ul>
                <li>Set your total wedding budget</li>
                <li>Add expenses by category</li>
                <li>Track spending with visual progress indicators</li>
                <li>See total spent and remaining budget</li>
                <li>Differentiate between paid and pending expenses</li>
              </ul>
            </div>
          }
        />
      </h1>

      {/* Budget Message */}
      <div className="budget-message-box">
        <div className="budget-message-percentage">{budgetMessage.percentage}</div>
        <div className="budget-message-text">{budgetMessage.message}</div>
      </div>


      <div className="budget-layout">
        {/* Left Panel: Category List */}
        <div className="budget-categories-panel">
          <button 
            className="budget-new-category-btn"
            onClick={() => setShowNewCategory(true)}
          >
            New Category
          </button>
          
          {showNewCategory && (
            <div className="budget-new-category-form">
              <input
                type="text"
                placeholder="Category name"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleCreateCategory()}
                autoFocus
              />
              <div className="budget-form-actions">
                <button onClick={handleCreateCategory}>Add</button>
                <button onClick={() => {
                  setShowNewCategory(false)
                  setNewCategoryName('')
                }}>Cancel</button>
              </div>
            </div>
          )}

          <div className="budget-categories-list">
            {Object.values(categories).map((category) => (
              <div
                key={category.name}
                className={`budget-category-item ${selectedCategory === category.name ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category.name)}
              >
                <span className="budget-category-name">{category.name}</span>
                <span className="budget-category-total">
                  {category.totalActual > 0 
                    ? formatCurrency(category.totalActual)
                    : category.totalEstimated > 0
                    ? formatCurrency(category.totalEstimated)
                    : formatCurrency(0)
                  }
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Center Panel: Category Details */}
        <div className="budget-details-panel">
          {selectedCategoryData ? (
            <>
              <div className="budget-details-header">
                <h2>
                  {selectedCategoryData.name}
                </h2>
                <div className="budget-details-summary">
                  <span>Estimated cost: {formatCurrency(selectedCategoryData.totalEstimated)}</span>
                  <span>Final cost: {formatCurrency(selectedCategoryData.totalActual)}</span>
                  <button 
                    className="budget-delete-category-btn"
                    onClick={handleDeleteCategory}
                  >
                    Delete
                  </button>
                </div>
              </div>

              <div className="budget-expenses-table">
                <table>
                  <thead>
                    <tr>
                      <th>CONCEPT</th>
                      <th>ESTIMATED COST</th>
                      <th>FINAL COST</th>
                      <th>PAID</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedCategoryData.items.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="budget-empty-state">
                          No expenses yet. Click "Add new expense" to add one.
                        </td>
                      </tr>
                    ) : (
                      selectedCategoryData.items.map((item) => {
                        const isEditing = editingItems.has(item.id)
                        const localItem = localItems[item.id] || {
                          concept: item.concept || '',
                          estimated_cost: item.estimated_cost || '',
                          actual_cost: item.actual_cost || '',
                          paid: item.paid || false
                        }
                        const isUpdating = updatingItems.has(item.id)

                        return (
                          <tr key={item.id}>
                            <td>
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={localItem.concept}
                                  onChange={(e) => handleLocalUpdate(item.id, 'concept', e.target.value)}
                                  className="budget-inline-input"
                                  disabled={isUpdating}
                                />
                              ) : (
                                <span>{item.concept || '—'}</span>
                              )}
                            </td>
                            <td>
                              {isEditing ? (
                                <input
                                  type="number"
                                  value={localItem.estimated_cost}
                                  onChange={(e) => handleLocalUpdate(item.id, 'estimated_cost', e.target.value)}
                                  className="budget-inline-input"
                                  step="0.01"
                                  min="0"
                                  disabled={isUpdating}
                                />
                              ) : (
                                <span>{formatCurrency(item.estimated_cost)}</span>
                              )}
                            </td>
                            <td>
                              {isEditing ? (
                                <input
                                  type="number"
                                  value={localItem.actual_cost || ''}
                                  onChange={(e) => handleLocalUpdate(item.id, 'actual_cost', e.target.value)}
                                  className="budget-inline-input"
                                  step="0.01"
                                  min="0"
                                  disabled={isUpdating}
                                />
                              ) : (
                                <span>{formatCurrency(item.actual_cost)}</span>
                              )}
                            </td>
                            <td>
                              {isEditing ? (
                                <input
                                  type="checkbox"
                                  checked={localItem.paid}
                                  onChange={(e) => handleLocalUpdate(item.id, 'paid', e.target.checked)}
                                  disabled={isUpdating}
                                />
                              ) : (
                                <span>{item.paid ? '✓' : '—'}</span>
                              )}
                            </td>
                            <td>
                              <div className="budget-actions-cell">
                                {isEditing ? (
                                  <>
                                    <button
                                      className="budget-save-btn"
                                      onClick={() => handleSaveExpense(item.id)}
                                      disabled={isUpdating}
                                    >
                                      {isUpdating ? 'Saving...' : 'Save'}
                                    </button>
                                    <button
                                      className="budget-cancel-btn"
                                      onClick={() => handleCancelEdit(item.id)}
                                      disabled={isUpdating}
                                    >
                                      Cancel
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      className="budget-edit-btn"
                                      onClick={() => handleStartEdit(item)}
                                    >
                                      Edit
                                    </button>
                                    <button
                                      className="budget-delete-btn"
                                      onClick={() => handleDeleteExpense(item.id)}
                                    >
                                      Delete
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        )
                      })
                    )}
                    <tr className="budget-total-row">
                      <td><strong>Total</strong></td>
                      <td><strong>{formatCurrency(selectedCategoryData.totalEstimated)}</strong></td>
                      <td><strong>{formatCurrency(selectedCategoryData.totalActual)}</strong></td>
                      <td><strong>{formatCurrency(selectedCategoryData.totalPaid)}</strong></td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <button
                className="budget-add-expense-btn"
                onClick={() => setShowNewExpense(true)}
              >
                Add new expense
              </button>

              {showNewExpense && (
                <div className="budget-new-expense-form">
                  <h3>Add New Expense</h3>
                  <div className="budget-form-group">
                    <label>Concept</label>
                    <input
                      type="text"
                      value={newExpense.concept}
                      onChange={(e) => setNewExpense({ ...newExpense, concept: e.target.value })}
                      placeholder="Expense name"
                    />
                  </div>
                  <div className="budget-form-group">
                    <label>Estimated Cost</label>
                    <input
                      type="number"
                      value={newExpense.estimated_cost}
                      onChange={(e) => setNewExpense({ ...newExpense, estimated_cost: e.target.value })}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                    />
                  </div>
                  <div className="budget-form-group">
                    <label>Actual Cost</label>
                    <input
                      type="number"
                      value={newExpense.actual_cost}
                      onChange={(e) => setNewExpense({ ...newExpense, actual_cost: e.target.value })}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                    />
                  </div>
                  <div className="budget-form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={newExpense.paid}
                        onChange={(e) => setNewExpense({ ...newExpense, paid: e.target.checked })}
                      />
                      Paid
                    </label>
                  </div>
                  <div className="budget-form-actions">
                    <button onClick={handleCreateExpense}>Add</button>
                    <button onClick={() => {
                      setShowNewExpense(false)
                      setNewExpense({ concept: '', estimated_cost: '', actual_cost: '', paid: false })
                    }}>Cancel</button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="budget-no-category">
              <p>Select a category from the left to view its details</p>
            </div>
          )}
        </div>

        {/* Right Panel: Budget Summary */}
        <div className="budget-summary-panel">
          <div className="budget-summary-header">
            <h2>My Budget</h2>
            <div className="budget-summary-actions">
              <button onClick={() => showAlert('Print functionality coming soon')} title="Print">Print</button>
              <button onClick={() => showAlert('Download functionality coming soon')} title="Download">Download</button>
            </div>
          </div>

          <div className="budget-summary-stats">
            <div className="budget-stat-item">
              <div className="budget-stat-content">
                <div className="budget-stat-label">ESTIMATED COST</div>
                <div className="budget-stat-value">
                  {formatCurrency(stats.totalBudget)}
                </div>
                {!showEditBudget ? (
                  <button 
                    className="budget-modify-link"
                    onClick={() => {
                      setShowEditBudget(true)
                      setNewBudget(stats.totalBudget.toString())
                    }}
                  >
                    Modify budget
                  </button>
                ) : (
                  <div className="budget-edit-form">
                    <input
                      type="number"
                      value={newBudget}
                      onChange={(e) => setNewBudget(e.target.value)}
                      step="0.01"
                      min="0"
                      autoFocus
                    />
                    <div className="budget-form-actions">
                      <button onClick={handleUpdateBudget}>Save</button>
                      <button onClick={() => {
                        setShowEditBudget(false)
                        setNewBudget('')
                      }}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="budget-stat-item">
              <div className="budget-stat-content">
                <div className="budget-stat-label">FINAL COST</div>
                <div className="budget-stat-value">
                  {formatCurrency(stats.totalActual)}
                </div>
                <div className="budget-stat-sub">
                  <div>Paid: {formatCurrency(stats.totalPaid)}</div>
                  <div>Pending: {formatCurrency(stats.totalPending)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Donut Chart */}
          <div className="budget-chart-section">
            <h3>How much does my wedding cost?</h3>
            {chartData.length > 0 ? (
              <div className="budget-chart-container">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius="40%"
                      outerRadius="70%"
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                    />
                    <Legend 
                      verticalAlign="bottom"
                      height={36}
                      formatter={(value) => value}
                      wrapperStyle={{ fontSize: '0.85rem' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="budget-chart-empty">
                <p>Add expenses to see the chart</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Panel: Budget Summary Table */}
      <div className="budget-summary-table-section">
        <div className="budget-summary-table-header">
          <h2>Budget</h2>
          <div className="budget-table-actions">
            <button onClick={() => showAlert('Export functionality coming soon')}>Export</button>
            <button onClick={async () => {
              const ok = await showConfirm('Are you sure you want to reset the entire budget? This will delete all expenses.', { title: 'Reset budget', confirmLabel: 'Reset', cancelLabel: 'Cancel', variant: 'danger' })
              if (ok) await showAlert('Reset functionality coming soon')
            }}>Reset budget</button>
            <button onClick={() => showAlert('Print functionality coming soon')}>Print</button>
            <button onClick={() => showAlert('Highlight functionality coming soon')}>Highlight</button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="budget-progress-bar">
          <div className="budget-progress-info">
            <span>{formatCurrency(stats.totalBudget, 2)} estimated</span>
            <span>{formatCurrency(stats.totalActual, 2)} spent</span>
            <span>{formatCurrency(Math.max(0, stats.remaining), 2)} under budget</span>
          </div>
          <div className="budget-progress-bar-container">
            <div 
              className="budget-progress-bar-fill"
              style={{ 
                width: `${Math.min(100, stats.percentage)}%`,
                backgroundColor: stats.percentage > 100 ? '#F44336' : '#4CAF50'
              }}
            ></div>
          </div>
        </div>

        {/* Summary Table */}
        <div className="budget-summary-table">
          <table>
            <thead>
              <tr>
                <th>Category</th>
                <th>Estimated</th>
                <th>Paid</th>
                <th>Difference</th>
              </tr>
            </thead>
            <tbody>
              {Object.values(categories).map((category) => {
                const difference = category.totalEstimated - (category.totalActual || 0)
                return (
                  <tr key={category.name}>
                    <td><strong>{category.name}</strong></td>
                    <td>{formatCurrency(category.totalEstimated, 2)}</td>
                    <td>{formatCurrency(category.totalPaid, 2)}</td>
                    <td className={difference >= 0 ? 'budget-positive' : 'budget-negative'}>
                      {difference >= 0 ? '+' : ''}{formatCurrency(difference, 2)}
                    </td>
                  </tr>
                )
              })}
              {Object.keys(categories).length === 0 && (
                <tr>
                  <td colSpan="4" className="budget-empty-state">
                    No categories yet. Create one to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default BudgetManager
